import AddTeamMemberForm from "@/components/AddTeamMemberForm";
import TransitionOverlay from "@/components/TransitionOverlay";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useRetainedValue } from "@/hooks/useRetainedValue";
import { useToast } from "@/hooks/use-toast";
import { PendingInviteWithId, useOrganization } from "@/hooks/useFirestore";
import {
  cancelPendingInvite,
  inviteOrganizationUser,
  removeOrganizationUser,
  updateOrganizationUser,
} from "@/services/firebase";
import { UserPermission } from "@/types/user";
import { Plus } from "lucide-react";
import { useState } from "react";
import TeamList from "../components/TeamList";

const Team = () => {
  const { users, pendingInvites, loading } = useOrganization();
  const { user, userRole } = useAuth();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<UserPermission | null>(null);
  const [userToRemove, setUserToRemove] = useState<UserPermission | null>(null);
  const [inviteToCancel, setInviteToCancel] = useState<PendingInviteWithId | null>(null);
  const [removingUser, setRemovingUser] = useState<{ firstName: string; lastName: string } | null>(null);
  const renderedUserToRemove = useRetainedValue(userToRemove);
  const renderedInviteToCancel = useRetainedValue(inviteToCancel);
  const { toast } = useToast();

  // Check if user can manage team (admin)
  const canManageTeam = userRole === "admin";

  const handleAddMember = async (
    newUser: Omit<UserPermission, "id" | "userRef" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy">
  ) => {
    const result = await inviteOrganizationUser(newUser.email, newUser.firstName, newUser.lastName, newUser.role);

    if (result.success) {
      setIsAddDialogOpen(false);
      toast({
        title: "Team member added",
        description: `${newUser.firstName} ${newUser.lastName} has been added to the team.`,
      });
    } else {
      toast({
        title: "Failed to add team member",
        description: result.error || "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const handleEditMember = async (updatedUser: UserPermission) => {
    const result = await updateOrganizationUser(updatedUser.id, {
      firstName: updatedUser.firstName,
      lastName: updatedUser.lastName,
      email: updatedUser.email,
      role: updatedUser.role,
      status: updatedUser.status,
    });

    if (result.success) {
      setUserToEdit(null);
      toast({
        title: "Team member updated",
        description: `${updatedUser.firstName} ${updatedUser.lastName}'s information has been updated.`,
      });
    } else {
      // Handle specific error codes with user-friendly messages
      const errorMessage =
        result.error?.code === "LAST_ADMIN"
          ? "Cannot change role: This is the only admin in the organization. Promote another user to admin first."
          : result.error?.message || "An unexpected error occurred";

      toast({
        title: "Failed to update team member",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleRemoveMember = (user: UserPermission) => {
    setUserToRemove(user);
  };

  const confirmRemoveMember = async () => {
    // Capture the user reference before Radix's onOpenChange clears it.
    // AlertDialogAction will call onOpenChange(false) → setUserToRemove(null)
    // after this onClick handler, so we grab the value now.
    const userRef = renderedUserToRemove;
    if (!userRef) return;

    // Don't manually set userToRemove to null here - let Radix's AlertDialogAction
    // handle the close via onOpenChange to ensure proper body pointer-events cleanup.

    // Show the loading overlay with user info
    setRemovingUser({ firstName: userRef.firstName, lastName: userRef.lastName });

    try {
      const result = await removeOrganizationUser(userRef.id);

      if (result.success) {
        toast({
          title: "Team member removed",
          description: `${userRef.firstName} ${userRef.lastName} has been removed from the team.`,
        });
      } else {
        // Handle specific error codes with user-friendly messages
        const errorMessage =
          result.error?.code === "LAST_ADMIN"
            ? "Cannot remove: This is the only admin in the organization. Promote another user to admin first."
            : result.error?.code === "CANNOT_REMOVE_SELF"
              ? "You cannot remove yourself from the organization."
              : result.error?.message || "An unexpected error occurred";

        toast({
          title: "Failed to remove team member",
          description: errorMessage,
          variant: "destructive",
        });
      }
    } finally {
      // Always hide the overlay when done
      setRemovingUser(null);
    }
  };

  const handleCancelInvite = (invite: PendingInviteWithId) => {
    setInviteToCancel(invite);
  };

  const confirmCancelInvite = async () => {
    // Capture the invite reference before Radix's onOpenChange clears it.
    const inviteRef = renderedInviteToCancel;
    if (!inviteRef) return;

    // Don't manually set inviteToCancel to null — let Radix's AlertDialogAction
    // handle the close via onOpenChange to avoid stale pointer-events on body.

    const result = await cancelPendingInvite(inviteRef.id);

    if (result.success) {
      toast({
        title: "Invite cancelled",
        description: `The invitation for ${inviteRef.firstName} ${inviteRef.lastName} has been cancelled.`,
      });
    } else {
      toast({
        title: "Failed to cancel invite",
        description: result.error || "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="container mx-auto py-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Team Members</CardTitle>
          {canManageTeam && (
            <Button
              variant="default"
              onClick={() => setIsAddDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Member
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <TeamList
            users={users}
            pendingInvites={pendingInvites}
            loading={loading}
            currentUserId={user?.uid}
            onRemoveMember={handleRemoveMember}
            onEditMember={handleEditMember}
            onCancelInvite={handleCancelInvite}
            canManageTeam={canManageTeam}
          />
        </CardContent>
      </Card>

      <Dialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
          </DialogHeader>
          <AddTeamMemberForm
            onSubmit={handleAddMember}
            onCancel={() => setIsAddDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!userToRemove}
        onOpenChange={() => setUserToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {renderedUserToRemove?.firstName} {renderedUserToRemove?.lastName} from the team? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemoveMember}
              className="bg-red-600 hover:bg-red-700">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!inviteToCancel}
        onOpenChange={() => setInviteToCancel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Invitation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel the invitation for {renderedInviteToCancel?.firstName} {renderedInviteToCancel?.lastName}?
              They will no longer be able to join the organization using this invite.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Invite</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmCancelInvite}
              className="bg-red-600 hover:bg-red-700">
              Cancel Invite
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TransitionOverlay
        open={!!removingUser}
        title="Removing team member..."
        subtitle={removingUser ? `Removing ${removingUser.firstName} ${removingUser.lastName} from the team...` : undefined}
      />
    </div>
  );
};

export default Team;
